import { useMemo, useState } from "react";
import {
    type ColumnDef,
    type ColumnFiltersState,
    type SortingState,
    type VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { Badge } from "@patient-watch/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@patient-watch/ui/tooltip";
import { useActionCopy } from "@/features/i18n/actionCopy";
import { useListCopy } from "@/features/i18n/listCopy";
import { formatRoleLabel, useProfileCopy } from "@/features/i18n/profileCopy";
import FavouriteUserStar from "./FavouriteUserStar";
import TeamMemberDialog from "./TeamMemberDialog";
import type { TeamMember, UserOrganisation } from "../api/selectTeamMembers";
import { ArrowUpDown, ChevronDown, Info, Lock, Pencil } from "lucide-react";
import { ui, cn } from "@patient-watch/ui";
import { useNavigate } from "@tanstack/react-router";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@patient-watch/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { AppListActionButton, AppListToolbar } from "@/components/common/AppListToolbar";
import { RoleBadge } from "@/components/common/RoleBadge";
import { useAuth } from "@/utils/auth";
import { TeamFilterDialog, type TeamFilters } from "./TeamFilterDialog";
import {
    isDefaultSecretaryForOrg,
    useSecretaryOwnerCount,
    type SecretaryOrganisationAssignment,
} from "../api/selectSecretaryOrganisations";
import {
    useClearDefaultSecretaryForOrganisation,
    useSetDefaultSecretaryForOrganisation,
} from "../api/useSecretaryOrganisationMutations";

export const TEAM_PREVIEW_LIMIT = 5;

interface Props {
    members: TeamMember[];
    currentUserOrganisations: UserOrganisation[];
    secretaryAssignments: SecretaryOrganisationAssignment[];
    variant?: "page" | "preview";
}

interface MemberOrg {
    organisation_id: string;
    organisation_name: string;
    role: string;
}

function isMemberSecretary(member: TeamMember): boolean {
    return Boolean(member.roles?.includes("SECRETARY"));
}

function sharedOrgs(member: TeamMember, currentUserOrganisations: UserOrganisation[]): MemberOrg[] {
    const currentIds = new Set(currentUserOrganisations.map((org) => org.organisation_id));
    return (((member.organisations as MemberOrg[]) || []).filter(
        (org) => currentIds.has(org.organisation_id) && org.role !== "PATIENT"
    ) ?? []) as MemberOrg[];
}

function rowMembership(member: TeamMember, currentUserOrganisations: UserOrganisation[]): MemberOrg | null {
    if (member.organisationId && member.organisationName) {
        return {
            organisation_id: member.organisationId,
            organisation_name: member.organisationName,
            role: member.roleInOrg ?? "",
        };
    }
    return sharedOrgs(member, currentUserOrganisations)[0] ?? null;
}

function memberDisplayName(member: TeamMember): string {
    return `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email || "";
}

function SortHeader({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-1 -ml-2 px-2 py-1 rounded font-medium text-muted-foreground hover:text-foreground text-xs uppercase tracking-wide"
        >
            {label}
            <ArrowUpDown size={12} />
        </button>
    );
}

function MemberEditIndicator({ member, isSecretary }: { member: TeamMember; isSecretary: boolean }) {
    const { data: secretaryOwnerCount = 0 } = useSecretaryOwnerCount(isSecretary ? member.user_id : "");
    const canEdit = isSecretary ? member.canEditName && secretaryOwnerCount <= 1 : member.canEditName;

    return (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="flex justify-center items-center rounded-md w-8 h-8 shrink-0">
                        {canEdit ? (
                            <Pencil className="w-4 h-4 text-primary" aria-hidden />
                        ) : (
                            <Lock className="w-4 h-4 text-muted-foreground" aria-hidden />
                        )}
                    </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                    {canEdit ? "You can edit this member's name and organisations" : "View only"}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function SecretaryDefaultCell({
    member,
    currentUserOrganisations,
    secretaryAssignments,
}: {
    member: TeamMember;
    currentUserOrganisations: UserOrganisation[];
    secretaryAssignments: SecretaryOrganisationAssignment[];
}) {
    const copy = useProfileCopy();
    const auth = useAuth();
    const setDefaultSecretary = useSetDefaultSecretaryForOrganisation();
    const clearDefaultSecretary = useClearDefaultSecretaryForOrganisation();
    const org = rowMembership(member, currentUserOrganisations);

    if (!org || org.role !== "SECRETARY") {
        return <span className="text-muted-foreground"> - </span>;
    }

    const isDefault = isDefaultSecretaryForOrg(secretaryAssignments, member.user_id, org.organisation_id);
    const isPending = setDefaultSecretary.isPending || clearDefaultSecretary.isPending;
    const payload = {
        secretaryId: member.user_id,
        ownerClinicianId: auth.id,
        organisationId: org.organisation_id,
    };

    return (
        <div className="flex items-center" onClick={(event) => event.stopPropagation()}>
            <ui.Switch
                color="indigo"
                checked={isDefault}
                disabled={isPending}
                aria-label={isDefault ? copy.clearDefaultSecretary : copy.setAsDefaultSecretary}
                data-testid="secretary-default-toggle"
                onCheckedChange={(checked) => {
                    if (checked) {
                        setDefaultSecretary.mutate(payload);
                    } else {
                        clearDefaultSecretary.mutate(payload);
                    }
                }}
            />
        </div>
    );
}

function TeamMobileCard({
    member,
    currentUserOrganisations,
    secretaryAssignments,
    onOpen,
}: {
    member: TeamMember;
    currentUserOrganisations: UserOrganisation[];
    secretaryAssignments: SecretaryOrganisationAssignment[];
    onOpen: () => void;
}) {
    const copy = useProfileCopy();
    const name = memberDisplayName(member) || "No name";
    const org = rowMembership(member, currentUserOrganisations);
    const role = org?.role || member.roles?.[0];
    const isSecretaryOrg = org?.role === "SECRETARY";

    return (
        <button
            type="button"
            className={cn(
                "block bg-card p-3 border border-border/60 rounded-lg outline-none w-full text-left",
                "shadow-sm transition-[box-shadow,border-color]",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "active:bg-muted/40 cursor-pointer"
            )}
            data-testid="team-table-row"
            aria-label={name}
            onClick={onOpen}
        >
            <div className="flex flex-col items-stretch gap-2 min-w-0">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex justify-center items-center bg-slate-200 rounded-full w-10 h-10 font-medium text-slate-600 text-sm shrink-0">
                        {(member.first_name?.[0] || member.email?.[0] || "?").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground text-base truncate leading-tight">{name}</h3>
                        <p className="text-muted-foreground text-xs truncate leading-tight">{member.email}</p>
                    </div>
                    <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
                        <FavouriteUserStar userId={member.user_id} favourite={member.isFavourite} />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                    {role ? <RoleBadge role={role} label={formatRoleLabel(role, copy)} /> : null}
                    {org ? (
                        <Badge variant="outline" className="text-2xs">
                            {org.organisation_name}
                        </Badge>
                    ) : null}
                    {isSecretaryOrg ? (
                        <div className="flex items-center gap-1.5">
                            <span className="text-2xs text-muted-foreground">{copy.secretaryDefaultBadge}</span>
                            <SecretaryDefaultCell
                                member={member}
                                currentUserOrganisations={currentUserOrganisations}
                                secretaryAssignments={secretaryAssignments}
                            />
                        </div>
                    ) : null}
                    {member.isInvitedByMe ? (
                        <Badge variant="outline" className="text-2xs">
                            {copy.invitedByYou}
                        </Badge>
                    ) : null}
                </div>
            </div>
        </button>
    );
}

const EMPTY_FILTERS: TeamFilters = { roles: [], organisations: [], favouritesOnly: false };

const PREVIEW_HIDDEN_COLUMNS: VisibilityState = {
    addedBy: false,
    defaultSecretary: false,
    edit: false,
};

const TeamTable = ({ members, currentUserOrganisations = [], secretaryAssignments = [], variant = "page" }: Props) => {
    const copy = useProfileCopy();
    const actionCopy = useActionCopy();
    const listCopy = useListCopy();
    const navigate = useNavigate();
    const isPreview = variant === "preview";
    const roleLabel = (role: string | null | undefined) => formatRoleLabel(role, copy);
    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
    const [filters, setFilters] = useState<TeamFilters>(EMPTY_FILTERS);
    const [sorting, setSorting] = useState<SortingState>([{ id: "organisation", desc: false }]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(isPreview ? PREVIEW_HIDDEN_COLUMNS : {});
    const [globalFilter, setGlobalFilter] = useState("");
    const tableMembers = isPreview ? members.slice(0, TEAM_PREVIEW_LIMIT) : members;

    const columns = useMemo<ColumnDef<TeamMember>[]>(
        () => [
            {
                id: "name",
                accessorFn: (member) => memberDisplayName(member),
                header: ({ column }) => (
                    <SortHeader
                        label={listCopy.name}
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    />
                ),
                cell: ({ row }) => {
                    const member = row.original;
                    const name = memberDisplayName(member) || "No name";
                    return (
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="flex justify-center items-center bg-slate-200 rounded-full w-8 h-8 font-medium text-slate-600 text-sm shrink-0">
                                {(member.first_name?.[0] || member.email?.[0] || "?").toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{name}</div>
                                <span className="text-slate-500 dark:text-slate-400 text-sm truncate">
                                    {member.email}
                                </span>
                            </div>
                        </div>
                    );
                },
            },
            {
                id: "role",
                accessorFn: (member) => rowMembership(member, currentUserOrganisations)?.role ?? "",
                header: listCopy.role,
                filterFn: (row, _id, value: string[]) => {
                    if (!value?.length) return true;
                    const role = rowMembership(row.original, currentUserOrganisations)?.role;
                    return Boolean(role && value.includes(role));
                },
                cell: ({ row }) => {
                    const role = rowMembership(row.original, currentUserOrganisations)?.role;
                    if (!role) return <span className="text-muted-foreground"> - </span>;
                    return <RoleBadge role={role} label={roleLabel(role)} />;
                },
            },
            {
                id: "organisation",
                accessorFn: (member) => rowMembership(member, currentUserOrganisations)?.organisation_name ?? "",
                header: ({ column }) => (
                    <SortHeader
                        label={listCopy.organisation}
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    />
                ),
                filterFn: (row, _id, value: string[]) => {
                    if (!value?.length) return true;
                    const org = rowMembership(row.original, currentUserOrganisations);
                    return Boolean(org && value.includes(org.organisation_id));
                },
                cell: ({ row }) => {
                    const org = rowMembership(row.original, currentUserOrganisations);
                    if (!org) return <span className="text-muted-foreground"> - </span>;
                    return (
                        <Badge variant="outline" className="text-2xs">
                            {org.organisation_name}
                        </Badge>
                    );
                },
            },
            {
                id: "addedBy",
                accessorFn: (member) => (member.isInvitedByMe ? "invited" : "other"),
                header: ({ column }) => (
                    <SortHeader
                        label={copy.columnAddedBy}
                        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                    />
                ),
                filterFn: (row, _id, value: string) => {
                    if (!value || value === "all") return true;
                    if (value === "invited") return row.original.isInvitedByMe;
                    if (value === "not-invited") return !row.original.isInvitedByMe;
                    return true;
                },
                cell: ({ row }) =>
                    row.original.isInvitedByMe ? (
                        <Badge variant="outline" className="text-2xs">
                            {copy.invitedByYou}
                        </Badge>
                    ) : (
                        <span className="text-muted-foreground"> - </span>
                    ),
            },
            {
                id: "defaultSecretary",
                accessorFn: (member) =>
                    isDefaultSecretaryForOrg(secretaryAssignments, member.user_id, member.organisationId)
                        ? "default"
                        : "",
                header: () => (
                    <div className="flex items-center gap-1">
                        <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                            {copy.columnDefaultSecretary}
                        </span>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="text-muted-foreground hover:text-foreground"
                                        aria-label={copy.secretaryDefaultColumnHelp}
                                    >
                                        <Info size={14} />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">{copy.secretaryDefaultColumnHelp}</TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                ),
                enableSorting: false,
                cell: ({ row }) => (
                    <SecretaryDefaultCell
                        member={row.original}
                        currentUserOrganisations={currentUserOrganisations}
                        secretaryAssignments={secretaryAssignments}
                    />
                ),
            },
            {
                id: "favourite",
                accessorFn: (member) => (member.isFavourite ? 1 : 0),
                header: () => <span className="sr-only">{copy.columnFavourite}</span>,
                filterFn: (row, _id, value: boolean) => {
                    if (!value) return true;
                    return row.original.isFavourite;
                },
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <FavouriteUserStar userId={row.original.user_id} favourite={row.original.isFavourite} />
                    </div>
                ),
            },
            {
                id: "edit",
                header: () => <span className="sr-only">{copy.edit}</span>,
                enableSorting: false,
                enableHiding: false,
                cell: ({ row }) => (
                    <MemberEditIndicator member={row.original} isSecretary={isMemberSecretary(row.original)} />
                ),
            },
        ],
        [copy, currentUserOrganisations, listCopy, roleLabel, secretaryAssignments]
    );

    const table = useReactTable({
        data: tableMembers,
        columns,
        state: { sorting, columnFilters, columnVisibility, globalFilter },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onGlobalFilterChange: setGlobalFilter,
        getRowId: (member) => `${member.user_id}:${member.organisationId ?? "none"}`,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn: (row, _columnId, filterValue) => {
            const search = String(filterValue ?? "")
                .trim()
                .toLowerCase();
            if (!search) return true;
            const member = row.original;
            const name = memberDisplayName(member).toLowerCase();
            const email = (member.email || "").toLowerCase();
            const org = (rowMembership(member, currentUserOrganisations)?.organisation_name ?? "").toLowerCase();
            return name.includes(search) || email.includes(search) || org.includes(search);
        },
    });

    const staffOrganisations = useMemo(() => {
        const seen = new Set<string>();
        return currentUserOrganisations.filter((org) => {
            if (org.role === "PATIENT" || seen.has(org.organisation_id)) return false;
            seen.add(org.organisation_id);
            return true;
        });
    }, [currentUserOrganisations]);

    const applyFilters = (next: TeamFilters) => {
        setFilters(next);
        table.getColumn("role")?.setFilterValue(next.roles.length ? next.roles : undefined);
        table.getColumn("organisation")?.setFilterValue(next.organisations.length ? next.organisations : undefined);
        table.getColumn("favourite")?.setFilterValue(next.favouritesOnly ? true : undefined);
    };

    return (
        <>
            {!isPreview && (
                <AppListToolbar
                    className="mb-4"
                    search={{
                        value: globalFilter,
                        onChange: setGlobalFilter,
                        placeholder: copy.searchTeamPlaceholder,
                        "aria-label": copy.searchTeamAria,
                    }}
                    filters={
                        <TeamFilterDialog filters={filters} organisations={staffOrganisations} onApply={applyFilters} />
                    }
                    desktopActions={
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <ui.Button size="sm" variant="outline" className="hidden md:inline-flex h-12">
                                    {actionCopy.columns} <ChevronDown className="ml-1 w-4 h-4" />
                                </ui.Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {table
                                    .getAllColumns()
                                    .filter((column) => column.getCanHide())
                                    .map((column) => (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            className="capitalize"
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) => column.toggleVisibility(!!value)}
                                        >
                                            {column.id === "name"
                                                ? listCopy.name
                                                : column.id === "role"
                                                  ? listCopy.role
                                                  : column.id === "organisation"
                                                    ? listCopy.organisation
                                                    : column.id === "addedBy"
                                                      ? copy.columnAddedBy
                                                      : column.id === "defaultSecretary"
                                                        ? copy.columnDefaultSecretary
                                                        : column.id === "favourite"
                                                          ? copy.columnFavourite
                                                          : column.id}
                                        </DropdownMenuCheckboxItem>
                                    ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    }
                    mobileAction={
                        <AppListActionButton
                            label={copy.inviteUser}
                            data-testid="team-invite-user-mobile"
                            layout="mobile"
                            onClick={() => navigate({ to: "/team/invite" })}
                        />
                    }
                />
            )}

            <div data-testid={isPreview ? "team-preview-table" : "team-table"}>
                <div className="md:hidden flex flex-col gap-2 w-full" data-testid="team-mobile-cards">
                    {table.getRowModel().rows.length ? (
                        table
                            .getRowModel()
                            .rows.map((row) => (
                                <TeamMobileCard
                                    key={row.id}
                                    member={row.original}
                                    currentUserOrganisations={currentUserOrganisations}
                                    secretaryAssignments={secretaryAssignments}
                                    onOpen={() => setSelectedMember(row.original)}
                                />
                            ))
                    ) : (
                        <div className="py-8 text-muted-foreground text-sm text-center">{copy.noTeamMembersFound}</div>
                    )}
                </div>
                <div
                    className={cn(
                        "hidden md:block overflow-x-auto",
                        isPreview ? "-mx-2" : "bg-white dark:bg-slate-950 border rounded-md overflow-hidden"
                    )}
                >
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.length ? (
                                table.getRowModel().rows.map((row) => {
                                    const member = row.original;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            tabIndex={0}
                                            role="button"
                                            onClick={() => setSelectedMember(member)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    setSelectedMember(member);
                                                }
                                            }}
                                            className="cursor-pointer"
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className={
                                                        cell.column.id === "favourite" || cell.column.id === "edit"
                                                            ? "align-middle"
                                                            : undefined
                                                    }
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-muted-foreground text-center"
                                    >
                                        {copy.noTeamMembersFound}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
            {!isPreview && (
                <div className="py-3 text-muted-foreground text-sm">
                    {copy.membersShown(table.getFilteredRowModel().rows.length)}
                </div>
            )}

            {selectedMember && (
                <TeamMemberDialog
                    open={!!selectedMember}
                    onOpenChange={(open) => !open && setSelectedMember(null)}
                    member={selectedMember}
                    currentUserOrganisations={currentUserOrganisations}
                />
            )}
        </>
    );
};

export default TeamTable;
